import { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";

import type { FormContextData, H3Data } from "@thalamos/aspire-common";
import {
  formHasSignatures,
  formIsCompleted,
  formIsRecordOfDetention,
  getBaseFormTemplate,
  getFullFormName,
  getLatestVersion,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../api.js";
import { LoggedInUserContext } from "../Contexts.js";
import { usePatientTimeline } from "../hooks/apiCalls.js";
import { routeFns } from "../routePaths.js";
import type { DefaultPageProps } from "./defaultProps.js";
import { RecordReasonDialog } from "./FormProgressPage/admission/RecordReasonDialog.js";
import {
  FormContextPdfViewer,
  VersionFormsPdfViewer,
} from "./FormProgressPage/helpers/FormContextPdfViewer.js";
import { partNeedingDoctorAccreditation } from "./FormProgressPage/helpers/partNeedingDoctorAccreditation.js";
import { reviewableForms } from "./FormProgressPage/helpers/reviewableForms.js";
import { isAmendForOtherUser } from "./FormProgressPage/helpers/workItemHelpers.js";
import { policeIncidentLogic } from "./FormProgressPage/police-incident/logic.js";
import { GmcNumberNeededDialog } from "./GmcNumberNeededDialog.js";
import {
  Banner,
  BannerList,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";

function getBannerTitle(
  fullFormName: string | null,
  formContext: FormContextData,
  formId: string | undefined,
  numOfFormParts: number | undefined,
) {
  const activeWorkItem = formContext.activeTeamworkWorkItem;
  const createdByName = `${activeWorkItem?.createdBy.userName} (${activeWorkItem?.createdBy.teamName})`;

  const matchingForm = activeWorkItem?.forms.find(
    (f) => f.formTemplate.id === formId,
  );
  const formHasOnePart = numOfFormParts === 1;
  const partMessage = !formHasOnePart
    ? ` part ${activeWorkItem?.formPart ?? ""} of `
    : "";

  // Admission
  if (!fullFormName) {
    return `${createdByName} has invited you to access the above patient's assessment documents.`;
  }

  if (activeWorkItem?.requestType === "amend") {
    const formattedReasons = activeWorkItem.requestReasons
      ?.map((r) => r.label)
      ?.join(", ");
    const h3Form = formContext.forms.find(
      (f) => formHasSignatures(f) && formIsRecordOfDetention(f),
    );
    const section = (getLatestVersion(h3Form)?.data as H3Data)?.[0]?.data
      .sectionValue;
    const timePeriod = section === "4" ? "72 hours" : "14 days";
    return [
      `${createdByName} has requested ${partMessage}${fullFormName} to be amended under Section 15 of the Act.`,
      `Reason(s): ${formattedReasons || ""}`,
      `Description: ${activeWorkItem.requestAdditionalInfo || ""}`,
      `The form can be amended within ${timePeriod} after admission if the form is found to be incorrect or defective. Please note that certain errors may invalidate the patient’s detention and cannot be corrected, a new form must be provided in these circumstances. Click accept if you need to amend this form. Click decline if you do not indent to amend this form.`,
    ];
  }

  // Standalone Form
  if (activeWorkItem?.requestType === "finalise") {
    // Don't mention 'completing' a form for MHA team finalisation requests
    return `${createdByName} has invited you to complete administrative scrutiny for ${fullFormName}. Click accept to scrutinise this form. Click decline if you do not intend to scrutinise the form.`;
  }

  if (activeWorkItem?.requestType === "complete") {
    return `${createdByName} has invited you to complete${partMessage} ${fullFormName}. Click accept to complete the form. Click decline if you do not intend to complete the form.`;
  }

  if (matchingForm && formIsCompleted(matchingForm)) {
    return `${createdByName} has invited you to access ${fullFormName}. Click accept to access form. Click decline if you do not intend to access the form.`;
  }
  return `${createdByName} has invited you to complete ${fullFormName}. Click accept to complete form. Click decline if you do not intend to complete the form`;
}

const amendDeclineReasons = [
  { value: "I believe this was sent in error" },
  {
    value: "Details on the form are correct",
    descriptionPrompt: "Please state the correct information",
    descriptionRequired: true,
  },
  {
    value: "Other",
    descriptionRequired: true,
  },
].map((o) => ({ ...o, label: o.value }));

const admissionDeclineOptions = [
  { value: "I believe this was sent in error" },
  {
    value: "Details on the form are incorrect",
    descriptionPrompt: "Please state the incorrect information",
    descriptionRequired: true,
  },
  {
    value: "Other",
    descriptionRequired: true,
  },
].map((o) => ({ ...o, label: o.value }));

const standaloneFormDeclineOptions = [
  { value: "I do not believe this is clinically appropriate" },
  { value: "I believe this was sent in error" },
  {
    value: "Details on the form are incorrect",
    descriptionPrompt: "Please state the incorrect information",
    descriptionRequired: true,
  },
  {
    value: "Other",
    descriptionRequired: true,
  },
].map((o) => ({ ...o, label: o.value }));

function ValidatePoliceIncidentReview({
  formContext,
}: {
  formContext: FormContextData;
}) {
  const navigate = useNavigate();
  const [{ loading, data: shareableIncidentData }] =
    apiHooks.vision.getIncidentForFormContext(formContext.id);

  const { isUserMhaAdmin, isIncidentInProgress } = policeIncidentLogic(
    formContext,
    shareableIncidentData,
  );

  useEffect(() => {
    if (!loading && shareableIncidentData) {
      if (!isUserMhaAdmin && isIncidentInProgress) {
        navigate(
          routeFns.visionPreviewIncident(shareableIncidentData.incidentId),
        );
      }
    }
  }, [loading, shareableIncidentData]);

  return <></>;
}

type GmcNumberNeeded = {
  partNumber: number;
  formContextId: string;
  patientId: string;
};

function FormReviewPageInner({
  formContext,
  onGmcNumberNeeded,
}: {
  formContext: FormContextData;
  onGmcNumberNeeded: (gmcNumberNeeded: GmcNumberNeeded) => void;
}) {
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);
  const currentUserId = userContext?.user?.id;
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);

  const { patientTimeline, reloadPatientTimeline } = usePatientTimeline({
    patientId: formContext.patientId,
  });

  const nhsNumber = patientTimeline?.patient?.nhsNumber ?? null;

  const myWorkItem = formContext.activeTeamworkWorkItem;
  const form =
    formContext.type === "standalone"
      ? formContext.forms[0]
      : myWorkItem?.formId
        ? formContext.forms.find((f) => f.id === myWorkItem?.formId)
        : null;
  const template = form ? getBaseFormTemplate(form) : null;
  const fullFormName = getFullFormName(template);
  const numOfFormParts = template?.parts.length;

  const completedForms = reviewableForms(myWorkItem?.formId, formContext);

  const hasCompletedForms = completedForms.length > 0;

  // Only default to the work item's form if it is actually reviewable (signed and
  // in the list). A work item can point at an in-progress form with no PDF — e.g.
  // a redo replacement awaiting completion — which is not in completedForms and
  // would otherwise be passed to the PDF viewer and crash it.
  const reviewableWorkItemFormId = completedForms.find(
    (f) => f.id === myWorkItem?.formId,
  )?.id;

  const [isPdfViewOpen, setIsPdfViewOpen] = useState<string | null>(
    reviewableWorkItemFormId ?? completedForms[0]?.id ?? null,
  );

  const isAmendRequestType = myWorkItem?.requestType === "amend";

  const isAmendForOther =
    !!currentUserId && isAmendForOtherUser(myWorkItem, currentUserId);
  const assignedDoctorName = myWorkItem?.assignedUser?.name ?? "another doctor";

  const options = isAmendRequestType
    ? amendDeclineReasons
    : formContext.type === "standalone"
      ? standaloneFormDeclineOptions
      : admissionDeclineOptions;

  return (
    <>
      {formContext.type === "police-incident" && (
        <ValidatePoliceIncidentReview formContext={formContext} />
      )}
      {rejectionDialogOpen && (
        <RecordReasonDialog
          title="Decline Invite"
          dropDownLabel="Enter a reason why you have chosen to decline this invitation"
          options={options}
          onClose={() => {
            setRejectionDialogOpen(false);
          }}
          onConfirmClick={(reason, reasonDescription) => {
            const onConfirmClick = async (
              reason: string,
              reasonDescription: string | undefined,
            ) => {
              const result = await api.work.reject(
                myWorkItem!.id,
                reason,
                reasonDescription,
              );
              if (result.status === 204) {
                renderSuccessToast({
                  message: `Request declined and sent back to ${myWorkItem?.createdBy.userName} (${myWorkItem?.createdBy.teamName})`,
                });

                if (isAmendRequestType) {
                  navigate(
                    routeFns.formContextPageSuccessDialog(
                      formContext.id,
                      formContext.patientId,
                      {
                        title: "Request declined ",
                        message: `You have successfully declined the request to amend a form under Section 15 of the Act. The requesting team has been notified by email of your decision.`,
                      },
                    ),
                    { replace: true },
                  );
                } else {
                  navigate(routeFns.home(), { replace: true });
                }
              } else {
                renderErrorToast({ message: "Failed to decline request" });
              }
            };
            void onConfirmClick(reason, reasonDescription);
          }}
        />
      )}
      <PatientBanner
        disableAutoMergeCheck
        isHidden
        patient={formContext.patient}
        patientTimeline={patientTimeline}
        nhsNumber={nhsNumber ?? undefined}
        reloadPatientTimeline={reloadPatientTimeline}
      />
      <Box sx={{ mb: 2 }}>
        {isAmendForOther ? (
          <Banner
            title={`${assignedDoctorName} has been asked to amend part 1 of this medical recommendation. Part 2 can be amended once ${assignedDoctorName} has completed their amend.`}
            bannerType={BannerList.WARNING}
          />
        ) : (
          <Banner
            body={getBannerTitle(
              fullFormName,
              formContext,
              template?.id,
              numOfFormParts,
            )}
            bannerType={BannerList.WARNING}
          />
        )}
      </Box>
      {isAmendRequestType && isPdfViewOpen && hasCompletedForms ? (
        <VersionFormsPdfViewer
          inModal={false}
          forms={completedForms}
          setFormId={setIsPdfViewOpen}
          formId={isPdfViewOpen}
          showPrevNextButtons={false}
        />
      ) : (
        <>
          {isPdfViewOpen && hasCompletedForms && (
            <Box
              sx={{ display: "flex", width: "100%", justifyContent: "center" }}
            >
              <Box sx={{ width: "750px" }}>
                <FormContextPdfViewer
                  hideUploadedFormInfo={formContext.type === "police-incident"}
                  inModal={false}
                  forms={completedForms}
                  setFormId={setIsPdfViewOpen}
                  formId={isPdfViewOpen}
                />
              </Box>
            </Box>
          )}
        </>
      )}

      {isAmendForOther ? (
        <FormFooterSection
          isSticky={true}
          isForcedSticky={true}
          saveLabel={"Go to form"}
          hideDiscard={true}
          onSave={() => {
            navigate(
              routeFns.formContextPage(formContext.id, formContext.patientId),
            );
          }}
          onCancel={() => {}}
        />
      ) : (
        <FormFooterSection
          isSticky={true}
          isForcedSticky={true}
          saveLabel={"Accept"}
          discardLabel={"Decline"}
          onSave={async () => {
            await api.work.accept(myWorkItem!.id);
            if (
              myWorkItem?.requestType === "complete" ||
              myWorkItem?.requestType === "amend"
            ) {
              // Requesting a draft for a part the user cannot sign would be refused, so explain why instead.
              const partNumber =
                userContext?.user && form
                  ? partNeedingDoctorAccreditation({
                      user: userContext.user,
                      form,
                    })
                  : undefined;
              if (partNumber !== undefined) {
                onGmcNumberNeeded({
                  partNumber,
                  formContextId: formContext.id,
                  patientId: formContext.patientId,
                });
                return;
              }

              const draftId = v4();
              const result = await api.drafts.create(
                draftId,
                // myWorkItem.formId will always be defined for a "complete" or "amend" request
                { formId: myWorkItem.formId! },
              );
              if (result.status === 201) {
                navigate(
                  routeFns.formDraftsComplete(draftId, formContext.patientId),
                );
              } else {
                navigate(
                  routeFns.formContextPage(
                    formContext.id,
                    formContext.patientId,
                  ),
                );
              }
            } else {
              navigate(
                routeFns.formContextPage(formContext.id, formContext.patientId),
              );
            }
          }}
          onCancel={() => {
            setRejectionDialogOpen(true);
          }}
        />
      )}
    </>
  );
}

function validateFormIsReadyForReview(
  formContext: FormContextData | undefined | null,
  formLoading: boolean,
  navigate: NavigateFunction,
) {
  if (!formLoading) {
    if (!formContext) {
      navigate(routeFns.notFound(), { replace: true });
    } else {
      if (
        !formContext.activeTeamworkWorkItem ||
        formContext.activeTeamworkWorkItem.status !== "pending"
      ) {
        navigate(
          routeFns.formContextPage(formContext.id, formContext.patientId),
        );
      }
    }
  }
}

export function FormReviewPage({}: DefaultPageProps) {
  let { formContextId } = useParams();
  const navigate = useNavigate();

  const userContext = useContext(LoggedInUserContext);
  const currentUserId = userContext?.user?.id;

  const [{ data: formContext, loading: formLoading, response }] =
    apiHooks.forms.getFormContext(formContextId!);

  // Held here rather than in the review page body, which unmounts while the form
  // context reloads after the invitation is accepted.
  const [gmcNumberNeeded, setGmcNumberNeeded] = useState<GmcNumberNeeded>();

  useEffect(() => {
    // Once accepted the invitation is no longer reviewable, but the explanation
    // must stay until the user chooses where to go.
    if (gmcNumberNeeded) return;
    validateFormIsReadyForReview(formContext, formLoading, navigate);
  }, [formContext, formLoading, navigate, gmcNumberNeeded]);

  return (
    <>
      {gmcNumberNeeded && (
        <GmcNumberNeededDialog
          partNumber={gmcNumberNeeded.partNumber}
          onAddGmcNumber={() => {
            if (currentUserId) {
              navigate(routeFns.userProfilePage(currentUserId));
            }
          }}
          onContinue={() => {
            navigate(
              routeFns.formContextPage(
                gmcNumberNeeded.formContextId,
                gmcNumberNeeded.patientId,
              ),
            );
          }}
        />
      )}
      {formLoading || response?.status !== 200 || !formContext ? null : (
        <FormReviewPageInner
          formContext={formContext}
          onGmcNumberNeeded={setGmcNumberNeeded}
        />
      )}
    </>
  );
}
